import { Autocomplete, Fade, Grid, TextField, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material';
import React, { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';
import AnalyticsService from '../../../../shared/services/analytics/analytics.service';
import { COMPARISON_EMISSION_KEY, getComparisonBarSeries, SeriesBar } from '../../../../shared/interfaces/analytics';
import { mergeComparisonByLabel } from '../../../../shared/utils/analytics-utils';
import { getMixedColorPalette } from '../../../../shared/utils/colors-utils';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import EmissionService from '../../../../shared/services/emission/emission.service';
import { IEmission } from '../../../../shared/services/emission/emission.model';
import GroupService from '../../../../shared/services/group/group.service';
import { IGroup } from '../../../../shared/services/group/group.model';
import { GraphBarChart } from '../graphs/graph-bar-chart';
import { GraphDonutChart } from '../graphs/graph-donut-chart';
import { DrillDownDrawer } from '../drill-down-drawer';
import { useDrillDown } from '../use-drill-down';
import { useDrillDimensionLabel } from '../use-drill-dimension-label';
import { useSkeletonDelay } from '../../../../hooks/use-skeleton-delay';
import { AnalyticsByGroupSkeleton } from './analytics-by-group-skeleton';
import { EmissionsTable } from '../../emissons-components/overview/emissions-table';

export const AnalyticsByGroup: FunctionComponent<{
  reportPeriodId: string;
  emissionType: 'marketBased' | 'locationBased';
  setLoaded?: React.Dispatch<React.SetStateAction<boolean>>;
  /** When set, charts carry the comparison period as an additional series/ring. */
  comparisonPeriod?: { id: string; label: string };
  periodLabel?: string;
}> = ({ reportPeriodId, emissionType, setLoaded, comparisonPeriod, periodLabel }) => {
  // Services
  const analyticsService = new AnalyticsService();
  const emissionService = new EmissionService();
  const groupService = GroupService.getInstance();
  // Context
  const reportPeriodContext = useContext(ReportPeriodContext);
  // State
  const [pieGroupEmissions, setPieGroupEmissions] = useState<any>();
  const [barCategoryEmissions, setBarCategoryEmissions] = useState<any>();
  // What the category chart breaks down by: individual categories, or the category groups they roll
  // up into ("Energy & Utilities", "Travel & Transport"). About 80 categories is a long chart; the
  // 15 groups give the overview, and clicking a group drills into its categories.
  const [categoryLevel, setCategoryLevel] = useState<'category' | 'categoryGroup'>('category');
  const [comparisonPieGroupEmissions, setComparisonPieGroupEmissions] = useState<any>();
  const [comparisonBarCategoryEmissions, setComparisonBarCategoryEmissions] = useState<any>();
  const [emissionRows, setEmissionRows] = useState<IEmission[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [allGroups, setAllGroups] = useState<IGroup[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [inputPage, setInputPage] = useState(0);
  const [perPage, setPerPage] = useState(5);
  const [orderBy, setOrderBy] = useState<string | undefined>();
  const [order, setOrder] = useState<'asc' | 'desc' | undefined>();
  // Colors
  const theme = useTheme();
  const colors = getMixedColorPalette(theme.palette.primary.main);
  // Loading
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const showSkeleton = useSkeletonDelay(isLoading, 250);
  // Variables
  const requiredReportPeriodId = reportPeriodId || reportPeriodContext?.lastSelectedReportId;
  // Click-to-drill: a stale selection clears when the period, basis or comparison changes.
  const scopeTwoMethod = emissionType === 'marketBased' ? 'market' : 'location';
  const drill = useDrillDown([requiredReportPeriodId, emissionType, comparisonPeriod?.id]);
  const dimLabel = useDrillDimensionLabel();

  useEffect(() => {
    if (!requiredReportPeriodId) {
      setIsLoading(false);
      if (setLoaded) setLoaded(true);
      return;
    }

    setIsLoading(true);
    const expectedRequests = comparisonPeriod ? 5 : 3;
    let completedRequests = 0;
    const checkLoadingStatus = () => {
      completedRequests += 1;
      if (completedRequests === expectedRequests) {
        setIsLoading(false);
        if (setLoaded) setLoaded(true);
      }
    };

    analyticsService
      .getPieData(requiredReportPeriodId, `?data=group&topN=all`)
      .then((response) => {
        setPieGroupEmissions(response);
        checkLoadingStatus();
      })
      .catch((err) => {
        console.log(err);
        checkLoadingStatus();
      });

    analyticsService
      .getBarData(requiredReportPeriodId, `?data=${categoryLevel}&topN=all`)
      .then((response) => {
        setBarCategoryEmissions(response);
        checkLoadingStatus();
      })
      .catch((err) => {
        console.log(err);
        checkLoadingStatus();
      });

    if (comparisonPeriod) {
      analyticsService
        .getPieData(comparisonPeriod.id, `?data=group&topN=all`)
        .then((response) => {
          setComparisonPieGroupEmissions(response);
          checkLoadingStatus();
        })
        .catch((err) => {
          console.log(err);
          setComparisonPieGroupEmissions(undefined);
          checkLoadingStatus();
        });

      analyticsService
        .getBarData(comparisonPeriod.id, `?data=${categoryLevel}&topN=all`)
        .then((response) => {
          setComparisonBarCategoryEmissions(response);
          checkLoadingStatus();
        })
        .catch((err) => {
          console.log(err);
          setComparisonBarCategoryEmissions(undefined);
          checkLoadingStatus();
        });
    } else {
      setComparisonPieGroupEmissions(undefined);
      setComparisonBarCategoryEmissions(undefined);
    }

    emissionService
      .getByDomainHighest(
        requiredReportPeriodId,
        'group',
        0,
        perPage,
        orderBy,
        order,
      )
      .then((emissionsResponse) => {
        const groupId = emissionsResponse.rows[0]?.groupId ?? '';
        setSelectedGroupId(groupId);
        setEmissionRows(emissionsResponse.rows);
        setTotalRecords(emissionsResponse.count);
        checkLoadingStatus();
      })
      .catch((err) => {
        console.log(err);
        checkLoadingStatus();
      });
  }, [requiredReportPeriodId, comparisonPeriod?.id, categoryLevel]);

  // Bar rows joined on category label so both periods render as grouped bars.
  const categoryBarDataset = useMemo(() => {
    const current = barCategoryEmissions?.[emissionType];
    if (!comparisonPeriod || !comparisonBarCategoryEmissions?.[emissionType]) return current;
    return mergeComparisonByLabel(current, comparisonBarCategoryEmissions[emissionType], 'totalEmission', COMPARISON_EMISSION_KEY);
  }, [barCategoryEmissions, comparisonBarCategoryEmissions, comparisonPeriod, emissionType]);

  const categoryBarSeries = comparisonPeriod && comparisonBarCategoryEmissions
    ? getComparisonBarSeries(periodLabel || 'Selected period', comparisonPeriod.label)
    : SeriesBar;

  useEffect(() => {
    groupService
      .getGroups()
      .then(setAllGroups)
      .catch((err) => console.log(err));
  }, []);

  const getEmissionsForGroup = (
    reportPeriodIdParam: string,
    groupId: string,
    page: number,
  ) => {
    if (!reportPeriodIdParam || !groupId) return;
    emissionService
      .getAll(
        reportPeriodIdParam,
        page,
        perPage,
        orderBy,
        order,
        `&groupId=${groupId}`,
      )
      .then((response) => {
        setEmissionRows(response.rows);
        setTotalRecords(response.count);
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    if (requiredReportPeriodId) {
      getEmissionsForGroup(requiredReportPeriodId, selectedGroupId, inputPage);
    }
  }, [inputPage, perPage, orderBy, order]);

  return (
    <>
      {isLoading && (
        <Fade in={showSkeleton} unmountOnExit>
          <div>
            <AnalyticsByGroupSkeleton />
          </div>
        </Fade>
      )}

      {!isLoading && (
        <Fade in={!showSkeleton} unmountOnExit>
          <div>
            <Grid container spacing={2}>
              {pieGroupEmissions && pieGroupEmissions[emissionType] && (
                <Grid
                  item
                  xl={4}
                  lg={4}
                  md={12}
                  sm={12}
                  xs={12}
                  sx={{ display: 'flex', flexDirection: 'column' }}
                >
                  <GraphDonutChart
                    enableTopN
                    defaultTopN={5}
                    colors={colors}
                    title="GHG Emission Breakdown by Group"
                    tooltip="The greenhouse gas emission breakdown per group for the selected report period"
                    dataset={pieGroupEmissions[emissionType]}
                    periodLabel={periodLabel}
                    comparison={comparisonPeriod && comparisonPieGroupEmissions?.[emissionType]
                      ? { dataset: comparisonPieGroupEmissions[emissionType], label: comparisonPeriod.label }
                      : undefined}
                    onSelect={(item) => drill.openDrill({ dimension: 'group', id: item.id, label: item.label, value: item.value })}
                  />
                </Grid>
              )}

              {barCategoryEmissions && barCategoryEmissions[emissionType] && (
                <Grid
                  item
                  xl={8}
                  lg={8}
                  md={12}
                  sm={12}
                  xs={12}
                  sx={{ display: 'flex', flexDirection: 'column' }}
                >
                  <GraphBarChart
                    enableTopN
                    defaultTopN={10}
                    title={categoryLevel === 'categoryGroup' ? 'GHG Emission Breakdown by Category Group' : 'GHG Emission Breakdown by Category'}
                    tooltip={categoryLevel === 'categoryGroup'
                      ? 'The greenhouse gas emission breakdown per category group for the selected report period. Select a group to see the categories inside it.'
                      : 'The greenhouse gas emission breakdown per category for the selected report period'}
                    headerAction={
                      <ToggleButtonGroup
                        value={categoryLevel}
                        exclusive
                        size='small'
                        onChange={(_event, value: 'category' | 'categoryGroup' | null) => { if (value) setCategoryLevel(value); }}
                      >
                        <ToggleButton value='category' sx={{ px: 1.5, py: 0.5, textTransform: 'none', fontSize: '0.8rem' }}>Category</ToggleButton>
                        <ToggleButton value='categoryGroup' sx={{ px: 1.5, py: 0.5, textTransform: 'none', fontSize: '0.8rem' }}>Category group</ToggleButton>
                      </ToggleButtonGroup>
                    }
                    dataset={categoryBarDataset}
                    series={categoryBarSeries}
                    dataKey="label"
                    layout="horizontal"
                    colors={colors}
                    onSelect={(item) => drill.openDrill({ dimension: categoryLevel, id: item.id, label: item.label, value: item.totalEmission })}
                  />
                </Grid>
              )}

              <Grid
                item
                xl={12}
                lg={12}
                md={12}
                sm={12}
                xs={12}
                sx={{ display: 'flex', flexDirection: 'column' }}
              >
                <Grid container>
                  <Grid item lg={8} sm={12}>
                    <Typography variant="subtitle1" sx={{ flex: 1, mb: 1 }}>
                      <b>Emission Records by Group</b>
                    </Typography>
                  </Grid>
                  <Grid item lg={4} sm={12}>
                    {allGroups.length > 0 && (
                      <Autocomplete
                        id="group-filter-autocomplete"
                        options={[...allGroups].sort((a, b) =>
                          (a.title ?? '').localeCompare(b.title ?? ''),
                        )}
                        getOptionLabel={(option) => option.title ?? ''}
                        value={
                          allGroups.find((g) => g.id === selectedGroupId) ?? null
                        }
                        onChange={(_, value) => {
                          const id = value?.id ?? '';
                          setSelectedGroupId(id);
                          if (id !== selectedGroupId) setInputPage(0);
                          if (requiredReportPeriodId)
                            getEmissionsForGroup(requiredReportPeriodId, id, 0);
                        }}
                        size="small"
                        sx={{ width: '100%', mb: 2 }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Filter"
                            variant="outlined"
                          />
                        )}
                      />
                    )}
                  </Grid>
                </Grid>
                <EmissionsTable
                  emissions={emissionRows}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={totalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  setOrderBy={setOrderBy}
                  setOrder={setOrder}
                  backLink={'/analytics/dashboard?tab=emissions&dim=group'}
                  backText="Back to analytics"
                />
              </Grid>
            </Grid>
          </div>
        </Fade>
      )}

      <DrillDownDrawer
        open={drill.open}
        onClose={drill.close}
        selection={drill.selection}
        reportPeriodId={requiredReportPeriodId || ''}
        periodLabel={periodLabel}
        scopeTwoMethod={scopeTwoMethod}
        comparisonReportPeriodId={comparisonPeriod?.id}
        dimensionLabel={dimLabel(drill.selection?.dimension)}
      />
    </>
  );
};
